/* eslint-disable @stylexjs/valid-styles */
import * as stylex from "@stylexjs/stylex";

export type PropsWithStyleX = {
    styleX?: stylex.StyleXStyles;
};

export type OmitStyles<T> = Omit<T, "style" | "className"> & PropsWithStyleX;

export type QueryXsMin = "@media (max-width: 480px)";
export type QueryXsMax = "@media (max-width: 759px)";
export type QuerySmMin = "@media (min-width: 760px)";
export type QuerySmMax = "@media (max-width: 989px)";
export type QueryMdMin = "@media (min-width: 990px)";
export type QueryMdMax = "@media (max-width: 1239px)";
export type QueryLgMin = "@media (min-width: 1240px)";
export type QueryLgMax = "@media (max-width: 1599px)";
export type QueryXlMin = "@media (min-width: 1600px)";

export type QueryLgMaxToSmMin = "@media (max-width: 1599px) and (min-width: 760px)";
export type QueryLgMaxToLgMin = "@media (max-width: 1599px) and (min-width: 1240px)";
export type QueryMdMaxToMdMin = "@media (max-width: 1239px) and (min-width: 990px)";
export type QueryMdMaxToSmMin = "@media (max-width: 1239px) and (min-width: 760px)";
export type QuerySmMaxToSmMin = "@media (max-width: 989px) and (min-width: 760px)";
// const sizes = {
//     screenXsMin: "480px",
//     screenSmMin: "760px",
//     screenMdMin: "990px",
//     screenLgMin: "1240px",
//     screenXlMin: "1600px",
//     screenXsMax: "759px",
//     screenSmMax: "989px",
//     screenMdMax: "1239px",
//     screenLgMax: "1599px",
// };

export const oldSizes = stylex.defineVars({
    col1: "8.33%",
    col2: "16.67%",
    col5: "41.67%",
});

export const colors = stylex.defineVars({
    b_Primary: "#E2E4E9",
    b_Secondary: "#F0F1F4",
    b_Tertiary: "#F6F7F9",
    b_centas: "#e4e4e4",
    b_Emphasis: "#FFFFFF",
    b_BrandMagenta: "#DC2664",
    b_HoverBrandMagenta: "#8d143c",
    b_BrandNavy: "#14284F",
    b_Accent: "#303437",
    b_Success: "#00CB53",
    b_Error: "#DC2626",

    bg_Primary: "#fff",
    bg_Secondary: "#F6F8F9",
    bg_Tertiary: "#F4F4F6",
    bg_AccentPrimary: "#DC2664",
    bg_BrandPrimary: "#1B2670",
    bg_Success: "#00CB53",
    bg_Error: "#DC2626",
    bg_AltDefault: "#1E2022",
    bg_BrandMagenta: "#DC2664",
    bg_SpecialToast: "hsla(0, 0%, 0%, 0.9)",
    bg_lightBlue: "#07c",

    fg_Primary: "#333",
    fg_Secondary: "#666",
    fg_Tertiary: "#999999",
    fg_Disabled: "#BBBCBF",
    fg_AltDefault: "#fff",
    fg_Accent: "#0077CC",
    fg_BrandBlue: "#1B2670",
    fg_Success: "#00CB53",
    fg_Error: "#DC2626",
    fg_BrandMagenta: "#dc2664",
    fg_BrandNavy: "#1b2670",
    fg_Content: "#37414e",
    grayMonoLight: "#f3f4f8",
    grayLighter: "#e5e5e5",
    grayLight: "#777",
    gray: "#555",
    grayDark: "#333",
    grayInactive: "#999",
});

export type StyleXProps<T> = OmitStyles<T> & PropsWithStyleX;

export const containerSize = stylex.defineVars({
    containerWidth: "90rem", //1200px
    containerWidthMobile: "100%",
    minContainerWidth: "61.25rem",
    minContainerWidthMobile: "100%",
    containerMaxWidth: "100%",
    containerMargin: "1.25rem auto 6.25rem",
    containerPadding: "0 0.625rem",
    containerHeight: "100%",
});

export const tooltipVars = stylex.defineVars({
    triangleSize: "7px",
    pLine: "1.5ch",
    pBlock: ".75ch",
});

const BASE_SPACING = 4 / 16;
export const ComposableSpacing = stylex.defineVars({
    s1: `${BASE_SPACING * 1}rem`,
    s2: `${BASE_SPACING * 2}rem`,
    s3: `${BASE_SPACING * 3}rem`,
    s4: `${BASE_SPACING * 4}rem`,
    s5: `${BASE_SPACING * 5}rem`,
    s6: `${BASE_SPACING * 6}rem`,
    s7: `${BASE_SPACING * 7}rem`,
    s8: `${BASE_SPACING * 8}rem`,
    s9: `${BASE_SPACING * 9}rem`,
    s10: `${BASE_SPACING * 10}rem`,
});

export const genericStyles = stylex.defineVars({
    boxShadow: "0px 0px 8px 0px #0000001A",
    borderRadiusS1: ComposableSpacing.s1,
    borderRadiusFullRound: "999999px",
});

export const globalAccessibility = stylex.create({
    ScreenReadersOnly: {
        border: 0,
        margin: "-1px",
        padding: "0",
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        clipPath: "inset(50%)",
        position: "absolute",
        whiteSpace: "nowrap",
        height: "1px",
        width: "1px",
    },
});

export const globalLimitStyles = stylex.create({
    TextOneLinesCap: {
        overflow: "hidden",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 1,
        display: "-webkit-box",
        textOverflow: "ellipsis",
        wordBreak: "break-word",
    },
    TextTwoLinesCap: {
        overflow: "hidden",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
        display: "-webkit-box",
        textOverflow: "ellipsis",
        wordBreak: "break-word",
    },
    TextThreeLinesCap: {
        overflow: "hidden",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 3,
        display: "-webkit-box",
        textOverflow: "ellipsis",
        wordBreak: "break-word",
    },
});

export const globalFontStyles = stylex.create({
    hXL: {
        fontSize: "2.5rem",
        lineHeight: 1.2,
    },

    hL: {
        fontSize: "2.25rem",
        lineHeight: 1.16,
    },

    hM: {
        fontSize: "2rem",
        lineHeight: 1.25,
    },

    hS: {
        fontSize: "1.5rem",
        lineHeight: 1.33,
    },

    hXS: {
        fontSize: "1.25rem",
        lineHeight: 1.4,
    },

    BodyXL: {
        fontSize: "1.125rem",
        lineHeight: 1.44,
    },

    BodyL: {
        fontSize: "1rem",
        lineHeight: 1.5,
    },

    BodyM: {
        fontSize: "0.875rem",
        lineHeight: 1.43,
    },

    BodyS: {
        fontSize: "0.75rem",
        lineHeight: 1.5,
    },

    span: {
        fontSize: "1rem",
        lineHeight: 1.25,
    },

    p: {
        fontSize: "1rem",
        lineHeight: 1.25,
    },

    primaryText: {
        margin: 0,
        padding: 0,
        color: colors.fg_Primary,
        fontWeight: 400,
        textTransform: "inherit",
    },
});
